// src/components/general/navbar/MyNavbar.js
import { useContext, useEffect, useState } from "react";
import { Container, Dropdown, Nav, Navbar, Offcanvas } from "react-bootstrap";
import {
  FaShoppingCart,
  FaUser,
  FaSignOutAlt,
  FaCog,
  FaHeart,
  FaBox,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "../../../assets/css/components/general/navbar/MyNavbar.css";
import Button from "../../../components/general/button/Button.js";
import logoImg from "../../../assets/images/logo/logo3.jpeg";
import MyContext from "../../../context/MyContext.js";

const MyNavbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  const {
    isLoggedIn,
    setIsLoggedIn,
    authToken,
    setAuthToken,
    setLoggedInCustomer,
    loggedInCustomer,
    baseUrl,
  } = useContext(MyContext);

  const [cartCount, setCartCount] = useState(0);

  const isActive = (path) => location.pathname === path;

  // ══════════════════════════════════════════════════════════
  // API FUNCTION — viewCartCount
  // ══════════════════════════════════════════════════════════
  const viewCartCount = () => {
    if (!loggedInCustomer?.id || !authToken) return;

    const formData = new FormData();

    axios
      .post(`${baseUrl}/customer/cart/viewAllCartProducts`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        console.log("Navbar cart count response:", response.data);
        if (response.data.success) {
          setCartCount(response.data.cartProducts.length);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── sirf tab restore karo jab authToken bhi mojood ho ──
  useEffect(() => {
    const savedToken = localStorage.getItem("authToken");
    const customer = localStorage.getItem("loggedInCustomer");

    if (savedToken && customer) {
      setLoggedInCustomer(JSON.parse(customer));
    } else {
      // token nahi hai to purana stale customer data bhi saaf kar dein
      localStorage.removeItem("loggedInCustomer");
      setLoggedInCustomer(null);
    }

    document.body.style.paddingTop = "77px";
    return () => {
      document.body.style.paddingTop = "0";
    };
  }, []);

  useEffect(() => {
    viewCartCount();
  }, [loggedInCustomer, authToken]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleNavClick = (path) => {
    navigate(path);
    handleClose();
  };

  // ── logout ab loggedInCustomer bhi puri tarah clear karta hai ──
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userPassword");
    localStorage.removeItem("loggedInCustomer");
    setAuthToken("");
    setLoggedInCustomer(null);
    setIsLoggedIn(false);
    navigate("/");
    handleClose();
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Shops", path: "/shops" },
    { name: "Products", path: "/products" },
    { name: "Business", path: "/business" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <Navbar
      expand="lg"
      fixed="top"
      className={`custom-navbar ${scrolled ? "navbar-scrolled" : ""}`}
    >
      <Container>
        {/* Logo */}
        <Navbar.Brand as={Link} to="/" className="navbar-logo">
          <div className="logo-wrapper">
            <img
              src={logoImg}
              alt="KisaanMart Logo"
              className="navbar-logo-img"
            />
            <div className="logo-text">
              <span className="logo-main">KisaanMart</span>
              <span className="logo-tagline">Agriculture Marketplace</span>
            </div>
          </div>
        </Navbar.Brand>

        {/* Mobile: Toggle Button */}
        <div className="d-flex d-lg-none align-items-center mb-0">
          <Navbar.Toggle
            aria-controls="navbar-offcanvas"
            onClick={handleShow}
            className="navbar-toggler-custom"
          />
        </div>

        {/* Desktop: Navbar Collapse */}
        <Navbar.Collapse id="navbar-collapse" className="d-none d-lg-flex">
          <Nav className="mx-auto navbar-nav-center">
            {navLinks.map((link) => (
              <Nav.Link
                key={link.name}
                as={Link}
                to={link.path}
                className={`nav-link-custom ${isActive(link.path) ? "active" : ""}`}
              >
                {link.name}
              </Nav.Link>
            ))}
          </Nav>

          <div className="navbar-actions">
            {isLoggedIn && (
              <div
                className="cart-btn-wrapper"
                onClick={() => navigate("/customer/cart")}
              >
                <div className="cart-icon-btn">
                  <FaShoppingCart />
                  {cartCount > 0 && (
                    <span className="cart-badge-count">{cartCount}</span>
                  )}
                </div>
              </div>
            )}

            {isLoggedIn ? (
              <Dropdown align="end" className="profile-dropdown">
                <Dropdown.Toggle as="div" className="profile-dropdown-toggle">
                  <div className="profile-avatar-wrapper">
                    <FaUser className="profile-avatar" />
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="profile-dropdown-menu">
                  <Dropdown.Item
                    onClick={() => navigate("/customer/profile")}
                    className="profile-dropdown-item"
                  >
                    <FaUser className="me-2" /> My Profile
                  </Dropdown.Item>

                  <Dropdown.Item
                    onClick={() => navigate("/customer/orders")}
                    className="profile-dropdown-item"
                  >
                    <FaBox className="me-2" /> My Orders
                  </Dropdown.Item>

                  <Dropdown.Item
                    onClick={() => navigate("/customer/wishlist")}
                    className="profile-dropdown-item"
                  >
                    <FaHeart className="me-2" /> Wishlist
                  </Dropdown.Item>

                  <Dropdown.Item
                    onClick={() => navigate("/customer/settings")}
                    className="profile-dropdown-item"
                  >
                    <FaCog className="me-2" /> Settings
                  </Dropdown.Item>

                  <Dropdown.Divider />

                  <Dropdown.Item
                    onClick={handleLogout}
                    className="profile-dropdown-item profile-dropdown-logout"
                  >
                    <FaSignOutAlt className="me-2" /> Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <>
                <button
                  className="login-btn-gradient"
                  onClick={() => navigate("/role-select")}
                >
                  Login/Register
                </button>
              </>
            )}
          </div>
        </Navbar.Collapse>

        {/* Mobile: Offcanvas Menu */}
        <Offcanvas
          show={show}
          onHide={handleClose}
          placement="end"
          className="mobile-offcanvas"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <div className="logo-wrapper">
                <div className="logo-icon-k">K</div>
                <div className="logo-text">
                  <span className="logo-main">KisaanMart</span>
                  <span className="logo-tagline">Agriculture Marketplace</span>
                </div>
              </div>
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <Nav className="flex-column mobile-nav">
              {navLinks.map((link) => (
                <Nav.Link
                  key={link.name}
                  onClick={() => handleNavClick(link.path)}
                  className="nav-link-mobile"
                >
                  {link.name}
                </Nav.Link>
              ))}

              {isLoggedIn && (
                <>
                  <Nav.Link
                    onClick={() => handleNavClick("/customer/cart")}
                    className="nav-link-mobile nav-link-cart"
                  >
                    <div className="mobile-cart-wrapper">
                      <FaShoppingCart className="mobile-cart-icon" />
                      <span>Shopping Cart</span>
                      {cartCount > 0 && (
                        <span className="cart-badge-count-mobile">{cartCount}</span>
                      )}
                    </div>
                  </Nav.Link>
                  <Nav.Link
                    onClick={() => handleNavClick("/customer/profile")}
                    className="nav-link-mobile"
                  >
                    <FaUser className="me-2" /> My Profile
                  </Nav.Link>
                  <Nav.Link
                    onClick={() => handleNavClick("/customer/orders")}
                    className="nav-link-mobile"
                  >
                    <FaBox className="me-2" /> My Orders
                  </Nav.Link>
                  <Nav.Link
                    onClick={() => handleNavClick("/customer/settings")}
                    className="nav-link-mobile"
                  >
                    <FaCog className="me-2" /> Settings
                  </Nav.Link>
                </>
              )}
            </Nav>

            <div className="mobile-auth-buttons">
              {isLoggedIn ? (
                <Button
                  variant="danger"
                  fullWidth
                  onClick={handleLogout}
                  icon={<FaSignOutAlt size={16} />}
                >
                  Logout
                </Button>
              ) : (
                <>
                  <button
                    className="login-btn-gradient"
                    onClick={() => handleNavClick("/customer/login")}
                  >
                    Customer
                  </button>

                  <Button
                    variant="gradient"
                    fullWidth
                    onClick={() => handleNavClick("/seller/login")}
                  >
                    Seller
                  </Button>

                  <Button
                    variant="gradient"
                    fullWidth
                    onClick={() => handleNavClick("/delivery/login")}
                  >
                    Delivery
                  </Button>
                </>
              )}
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </Container>
    </Navbar>
  );
};

export default MyNavbar;
